import { useState } from "react";
import { CalendarCheck, Loader2, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { PageHero } from "@/components/site/PageHero";
import { Chips } from "@/components/site/Chips";
import { FileUploader } from "@/components/site/FileUploader";
import { AudioRecorder } from "@/components/site/AudioRecorder";
import { SelectInput, TextArea, TextInput } from "@/components/site/Field";
import { api, errMsg, fmtDay, todayISO } from "@/lib/api";
import { LOCAIS, waLink } from "@/lib/content";
import { drWhatsappText } from "@/lib/whatsapp";
import { WhatsAppIcon } from "@/components/site/WhatsAppFloat";

const QUANDO = ["Ao ligar o veículo", "Em marcha lenta", "Ao acelerar", "Ao frear", "Em curvas", "Em lombadas ou buracos", "Com ar-condicionado ligado", "Com som ligado", "Sempre"];
const VELOCIDADE = ["Parado", "Até 20 km/h", "20 a 40 km/h", "40 a 60 km/h", "60 a 80 km/h", "80 a 100 km/h", "Acima de 100 km/h", "Qualquer velocidade"];
const PISO = ["Asfalto liso", "Asfalto irregular", "Paralelepípedo", "Terra / cascalho", "Lombadas", "Qualquer piso"];
const CONDICOES = ["Motor frio", "Motor quente", "Dias quentes", "Dias frios", "Com chuva", "Com o veículo carregado"];

const EMPTY = { nome: "", telefone: "", email: "", cidade: "", estado: "", veiculo: "", descricao: "", velocidade: "", condicoes_txt: "", data_inspecao: "", periodo: "", local_inspecao: "" };

const Block = ({ n, title, children }) => (
  <div className="card-tech p-6 sm:p-8">
    <p className="mb-5 flex items-center gap-3 font-heading text-lg font-bold uppercase text-white"><span className="font-mono text-sm text-red-500">{n}</span>{title}</p>
    {children}
  </div>
);

const Result = ({ dr, onNew }) => (
  <div className="card-tech mx-auto max-w-3xl border-red-900/60 p-8" data-testid="dr-result">
    <CalendarCheck className="h-10 w-10 text-red-500" />
    <p className="eyebrow mt-4">Protocolo {dr.protocolo}</p>
    <h2 className="mt-2 text-2xl font-bold uppercase text-white">Diagnóstico preliminar registrado</h2>
    <p className="mt-3 text-slate-300">{dr.pre_diagnostico.conclusao}</p>
    <p className="mt-2 text-sm text-slate-400">Inspeção solicitada para <b className="text-white">{fmtDay(dr.data_inspecao)}</b> {dr.periodo} — {dr.local_inspecao || "local a combinar"}.</p>
    {dr.pre_diagnostico.areas_a_inspecionar.length > 0 && (
      <div className="mt-5">
        <p className="field-label">Áreas a inspecionar (com base nos problemas indicados)</p>
        <div className="flex flex-wrap gap-1.5">{dr.pre_diagnostico.areas_a_inspecionar.map((a) => <span key={a} className="border border-slate-700 px-2 py-0.5 text-xs text-slate-300">{a}</span>)}</div>
      </div>
    )}
    <div className="mt-8 flex flex-wrap gap-3">
      <a href={waLink(drWhatsappText(dr))} target="_blank" rel="noreferrer" className="btn-red" data-testid="dr-send-whatsapp"><WhatsAppIcon className="h-4 w-4" /> Enviar ao WhatsApp do Nillo</a>
      <button onClick={onNew} className="btn-line" data-testid="dr-new">Nova solicitação</button>
    </div>
  </div>
);

export default function DrRuidos() {
  const { config } = useApp();
  const [f, setF] = useState(EMPTY);
  const [quando, setQuando] = useState([]);
  const [piso, setPiso] = useState([]);
  const [cond, setCond] = useState([]);
  const [probs, setProbs] = useState([]);
  const [files, setFiles] = useState({ audio: [], video: [], foto: [] });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const problemas = (config?.items || []).filter((i) => i.group === "D");

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nome || !f.telefone || !f.cidade || !f.veiculo || !f.descricao || !f.data_inspecao) {
      toast.error("Preencha os campos obrigatórios (*)");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...f, quando_ocorre: quando.join(", "), piso: piso.join(", "),
        condicoes: [...cond, f.condicoes_txt].filter(Boolean).join(", "),
        problemas: problemas.filter((p) => probs.includes(p.nome)).map((p) => p.key),
        arquivos: [...files.audio, ...files.video, ...files.foto].map((x) => x.id),
      };
      const { data } = await api.post("/dr-ruidos", payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setResult(null); setF(EMPTY); setQuando([]); setPiso([]); setCond([]); setProbs([]); setFiles({ audio: [], video: [], foto: [] }); };

  return (
    <div data-testid="page-dr-ruidos">
      <PageHero eyebrow="Dr. Ruídos" title="Barulho difícil de encontrar?">
        Contrate o Dr. Ruídos para investigação das possíveis causas do problema e definição de possíveis soluções. Descreva o ruído, envie áudio, vídeo e fotos e agende a inspeção do veículo.
      </PageHero>
      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
        {result ? <Result dr={result} onNew={reset} /> : (
          <form onSubmit={submit} className="space-y-6" data-testid="dr-form">
            <Block n="01" title="Seus dados">
              <div className="grid gap-4 sm:grid-cols-2">
                <TextInput label="Nome completo" required value={f.nome} onChange={set("nome")} testid="dr-nome" />
                <TextInput label="Telefone / WhatsApp" required value={f.telefone} onChange={set("telefone")} testid="dr-telefone" placeholder="(21) 90000-0000" />
                <TextInput label="E-mail" type="email" value={f.email} onChange={set("email")} testid="dr-email" />
                <TextInput label="Veículo (marca, modelo, ano)" required value={f.veiculo} onChange={set("veiculo")} testid="dr-veiculo" />
                <TextInput label="Cidade" required value={f.cidade} onChange={set("cidade")} testid="dr-cidade" />
                <TextInput label="Estado" value={f.estado} onChange={set("estado")} testid="dr-estado" maxLength={2} placeholder="RJ" />
              </div>
            </Block>
            <Block n="02" title="Sobre o ruído">
              <div className="space-y-5">
                <TextArea label="Descrição do ruído" required rows={4} value={f.descricao} onChange={set("descricao")} testid="dr-descricao" placeholder="Ex.: estalo seco no lado direito do painel..." />
                <div><span className="field-label">Quando ocorre</span><Chips options={QUANDO} value={quando} onChange={setQuando} testid="dr-quando" /></div>
                <SelectInput label="Velocidade aproximada" value={f.velocidade} onChange={set("velocidade")} options={VELOCIDADE} testid="dr-velocidade" />
                <div><span className="field-label">Piso onde ocorre</span><Chips options={PISO} value={piso} onChange={setPiso} testid="dr-piso" /></div>
                <div><span className="field-label">Temperatura ou condições específicas</span><Chips options={CONDICOES} value={cond} onChange={setCond} testid="dr-condicoes" /></div>
                <TextInput label="Outras condições" value={f.condicoes_txt} onChange={set("condicoes_txt")} testid="dr-condicoes-txt" />
                <div><span className="field-label">Problemas relacionados (opcional)</span><Chips options={problemas.map((p) => p.nome)} value={probs} onChange={setProbs} testid="dr-problemas" /></div>
              </div>
            </Block>
            <Block n="03" title="Áudio, vídeo e fotografias">
              <div className="grid gap-5 md:grid-cols-3">
                <div>
                  <AudioRecorder onUploaded={(x) => setFiles((s) => ({ ...s, audio: [...s.audio, x] }))} />
                  <FileUploader label="Áudio" accept="audio/*" files={files.audio} onChange={(v) => setFiles((s) => ({ ...s, audio: v }))} testid="dr-upload-audio" />
                </div>
                <FileUploader label="Vídeo" accept="video/*" camera files={files.video} onChange={(v) => setFiles((s) => ({ ...s, video: v }))} testid="dr-upload-video" />
                <FileUploader label="Fotografias" accept="image/*" camera files={files.foto} onChange={(v) => setFiles((s) => ({ ...s, foto: v }))} testid="dr-upload-foto" />
              </div>
            </Block>
            <Block n="04" title="Agendamento da inspeção do veículo">
              <div className="grid gap-4 sm:grid-cols-3">
                <TextInput label="Data desejada" required type="date" min={todayISO()} value={f.data_inspecao} onChange={set("data_inspecao")} testid="dr-data-inspecao" />
                <SelectInput label="Período" value={f.periodo} onChange={set("periodo")} options={["Manhã", "Tarde"]} testid="dr-periodo" />
                <SelectInput label="Local" value={f.local_inspecao} onChange={set("local_inspecao")} options={LOCAIS} testid="dr-local" />
              </div>
              <p className="mt-4 text-xs text-slate-500">A data será confirmada pelo Nillo via WhatsApp após análise do caso e do local.</p>
            </Block>
            <button type="submit" disabled={busy} className="btn-red w-full sm:w-auto" data-testid="dr-ruidos-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Stethoscope className="h-4 w-4" />} Gerar diagnóstico preliminar e agendar inspeção
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
